import React, { useState, useEffect } from 'react'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import OptimizedImage from './OptimizedImage'
import LanguageSwitcher from './LanguageSwitcher'
import { motion } from 'framer-motion'

const Header = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const location = useLocation()
  const isHomePage = location.pathname === '/'

  useEffect(() => {
    const sentinel = document.getElementById('scroll-sentinel')
    if (!sentinel) {
      // Fallback for pages where sentinel might not exist (though it should in MainWebsite)
      const handleScroll = () => setScrolled(window.scrollY > 20)
      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => window.removeEventListener('scroll', handleScroll)
    }

    const observer = new IntersectionObserver(([entry]) => {
      setScrolled(!entry.isIntersecting)
    }, { threshold: 0 })

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])
  
  const navItems = [
    { 
      name: 'About Trust', 
      type: 'dropdown',
      items: [
        { name: 'About Trust', href: '/about-trust' },
        { name: 'History', href: '/about-trust#history' },
        { name: 'Trust Details', href: '/about-trust#trusts' },
        { name: 'Founder', href: '/about-trust#founder' },
        { name: 'Chairman Message', href: '/about-trust#chairman' }
      ]
    },
    { 
      name: 'Campus', 
      type: 'dropdown',
      items: [
        { name: 'Campus Overview', href: '/campus' },
        { name: 'Skill Based Activity', href: '/skill-based-activity' },
        { name: 'Campus Directory', href: '/campus-directory' }
      ]
    },
    { 
      name: 'Academic', 
      type: 'dropdown',
      items: [
        { name: 'Institutions', href: '/institutions' },
        { name: 'Academic Programs', href: '/academic-programs' },
        { name: 'Faculty & Staff', href: '/staff-details' },
        { name: 'Annual Planner', href: '/annual-planner' }
      ]
    },
    { 
      name: isHomePage ? 'Gallery' : 'Gallery', 
      href: isHomePage ? '#gallery' : '/#gallery',
      type: 'link'
    },
    { 
      name: 'Alumni', 
      type: 'dropdown',
      items: [
        { name: 'Alumni Corner', href: '/alumni-corner' },
        { name: 'Alumni Stories', href: '/alumni-stories' }
      ]
    },
    { 
      name: 'Contact', 
      href: '/contact-us',
      type: 'link'
    }
  ]

  return (
    <motion.header
      initial={{ y: '-100%' }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
      className={`fixed top-0 left-0 right-0 w-full z-50 transition-[background-color,box-shadow] duration-300 will-change-transform ${
        scrolled ? 'bg-white shadow-lg' : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <nav className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10" aria-label="Primary">
        <div className="flex items-center justify-between h-15 sm:h-17 lg:h-20 max-w-7xl mx-auto">
          {/* Logo and School Name */}
          <Link 
            to="/" 
            className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition-opacity duration-300 flex-shrink-0"
            onClick={() => window.scrollTo(0, 0)}
          >
            <OptimizedImage
              src="/favicons/favicon-192x192.png"
              alt="Dr. Haribhai Godhani Campus Logo" 
              className="h-7 w-7 sm:h-8 sm:w-8 lg:h-12 lg:w-12 rounded-full object-cover flex-shrink-0"
              loading="eager"
              width={70}
              height={70}
            />
            <span className="text-base sm:text-lg lg:text-xl font-bold text-primary-dark font-poppins">
              DHGC
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-5 xl:space-x-7">
            {navItems.map((item) => (
              item.type === 'dropdown' ? (
                <div 
                  key={item.name}
                  className="relative group"
                  onMouseEnter={() => setOpenDropdown(item.name)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className="text-gray-700 hover:text-primary-dark font-medium transition-colors duration-300 flex items-center space-x-1 text-xs xl:text-sm whitespace-nowrap">
                    <span>{item.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className={`absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 transition-all duration-300 ${openDropdown === item.name ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                    {item.items.map((subItem) => (
                      subItem.isExternal ? (
                        <a
                          key={subItem.name}
                          href={subItem.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-4 py-2 text-gray-700 hover:bg-primary-dark/5 hover:text-primary-dark transition-all duration-300 relative group rounded-md"
                          onClick={() => setOpenDropdown(null)}
                        >
                          <span className="relative inline-block">
                            {subItem.name}
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-dark transition-all duration-300 group-hover:w-full"></span>
                          </span>
                        </a>
                      ) : (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className="block px-4 py-2 text-gray-700 hover:bg-primary-dark/5 hover:text-primary-dark transition-all duration-300 relative group rounded-md"
                          onClick={(e) => {
                            setOpenDropdown(null)
                            if (subItem.href.includes('#')) {
                              const hash = subItem.href.split('#')[1]
                              if (location.pathname === subItem.href.split('#')[0] || (location.pathname === '/about-trust' && subItem.href.startsWith('/about-trust#'))) {
                                e.preventDefault()
                                setTimeout(() => {
                                  const element = document.getElementById(hash)
                                  if (element) {
                                    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                  }
                                }, 100)
                              }
                            }
                          }}
                        >
                          <span className="relative inline-block">
                            {subItem.name}
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-dark transition-all duration-300 group-hover:w-full"></span>
                          </span>
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              ) : (
                item.href.startsWith('#') ? (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-gray-700 hover:text-primary-dark font-medium transition-colors duration-300 relative group text-xs xl:text-sm whitespace-nowrap"
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-dark transition-all duration-300 group-hover:w-full"></span>
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="text-gray-700 hover:text-primary-dark font-medium transition-colors duration-300 relative group text-xs xl:text-sm whitespace-nowrap"
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-dark transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                )
              )
            ))}
            <LanguageSwitcher className="mx-0.5 xl:mx-1" />
            <a 
              href={isHomePage ? "#admissions" : "/#admissions"}
              className="btn-primary hover:scale-105 inline-block text-xs xl:text-sm px-3 xl:px-5 py-2 xl:py-2.5 whitespace-nowrap"
            >
              Admissions Open
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-md text-gray-700 hover:text-primary-dark hover:bg-gray-100 transition-colors flex-shrink-0"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
              {navItems.map((item) => (
                item.type === 'dropdown' ? (
                  <div key={item.name}>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:text-primary-dark hover:bg-primary-dark/5 font-medium transition-all duration-300 flex items-center justify-between rounded-lg text-base"
                      onClick={() => setOpenDropdown(openDropdown === item.name ? null : item.name)}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === item.name && (
                      <div className="pl-6 space-y-1">
                        {item.items.map((subItem) => (
                          subItem.isExternal ? (
                            <a
                              key={subItem.name}
                              href={subItem.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-4 py-2.5 text-sm text-gray-600 hover:bg-primary-dark/5 hover:text-primary-dark transition-all duration-300 relative group rounded-lg"
                              onClick={() => {
                                setIsOpen(false)
                                setOpenDropdown(null)
                              }}
                            >
                              <span className="relative inline-block">
                                {subItem.name}
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-dark transition-all duration-300 group-hover:w-full"></span>
                              </span>
                            </a>
                          ) : (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="block px-4 py-2.5 text-sm text-gray-600 hover:bg-primary-dark/5 hover:text-primary-dark transition-all duration-300 relative group rounded-lg"
                              onClick={(e) => {
                                setIsOpen(false)
                                setOpenDropdown(null)
                                if (subItem.href.includes('#')) {
                                  const hash = subItem.href.split('#')[1]
                                  if (location.pathname === subItem.href.split('#')[0] || (location.pathname === '/about-trust' && subItem.href.startsWith('/about-trust#'))) {
                                    e.preventDefault()
                                    setTimeout(() => {
                                      const element = document.getElementById(hash)
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                      }
                                    }, 100)
                                  }
                                }
                              }}
                            >
                              <span className="relative inline-block">
                                {subItem.name}
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-dark transition-all duration-300 group-hover:w-full"></span>
                              </span>
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  item.href.startsWith('#') ? (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block px-4 py-3 text-gray-700 hover:text-primary-dark hover:bg-primary-dark/5 font-medium transition-all duration-300 rounded-lg text-base"
                      onClick={() => {
                        setIsOpen(false)
                        setOpenDropdown(null)
                      }}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="block px-4 py-3 text-gray-700 hover:text-primary-dark hover:bg-primary-dark/5 font-medium transition-all duration-300 rounded-lg text-base"
                      onClick={() => {
                        setIsOpen(false)
                        setOpenDropdown(null)
                      }}
                    >
                      {item.name}
                    </Link>
                  )
                )
              ))}
              <div className="px-3 py-2">
                <LanguageSwitcher />
              </div>
              <div className="px-3 py-2 flex justify-center">
                <a
                  href={isHomePage ? "#admissions" : "/#admissions"}
                  className="btn-primary inline-block text-sm px-6 py-3"
                  onClick={() => {
                    setIsOpen(false)
                    setOpenDropdown(null)
                  }}
                >
                  Admissions Open
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </motion.header>
  )
}

export default Header
